import React, { Fragment, memo, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { getPartnerId } from 'src/selectors/user'
import {
  FetchFundingInvoice,
  FetchFundingSettings,
  FetchPaymentsSummary,
  SubmitFundingMethodRequest,
} from 'src/constants/actionTypes'
import { handleApiError } from 'src/utils/errorHandlers'
import { ProcessPaymentsButton } from './ProcessPaymentsButton'
import {
  FundingInvoice,
  FundingMethod,
  FundingSettings,
  FundingSettingsBankAccountDetails,
  PartnerPaymentProvider,
  PaymentsSummary,
} from 'src/interfaces/payment'
import { UserPermission } from 'src/constants/user'
import { ProtectedWithPermission } from '../shared/ProtectedWithPermission'
import { AddPaymentsActionsButton } from './add-payments/AddPaymentsActionsButton'
import { MarkPaidMinor } from '@shopify/polaris-icons'
import { FundingType, FundingInvoiceStatus } from 'src/constants/payment/enums'
import { useModalOpen } from 'src/hooks/useModalOpen'
import tw from 'twin.macro'
import { BankFundConfirmModal } from './fund-payments/BankFundConfirmModal'
import { BankFundInProgressModal } from './fund-payments/BankFundInProgressModal'
import { ChooseFundMethodModal } from './fund-payments/ChooseFundMethodModal'
import { InstantFundConfirmModal } from './fund-payments/InstantFundConfirmModal'
import { InstantFundInProgressModal } from './fund-payments/InstantFundInProgressModal'
import { Button } from '../shared/Button'
import { isNonNullableArray } from 'src/utils/baseUtils'
import { usePolling } from 'src/utils/hooks/usePolling'
import { NothingToFundModal } from './fund-payments/NothingToFundModal'
import { UploadPaymentsCSVModal } from './add-payments/UploadPaymentsCSVModal'
import { AddSinglePaymentModal } from './add-payments/AddSinglePaymentModal'
import { ReloadWrapperContext } from 'src/components/wrapper/reload-wrapper/ReloadWrapper.context'
import { PaymentsSummaryModal } from './fund-payments/PaymentsSummaryModal'
import { AddMCPaymentsActionsButton } from '../mc-payment/add-payments/AddMCPaymentsActionsButton'
import { ProcessMCPaymentsButton } from '../mc-payment/ProcessMCPaymentsButton'
import { UploadMCPaymentsCSVModal } from '../mc-payment/add-payments/UploadMCPaymentsCSVModal'
import { AddSingleMCPaymentModal } from '../mc-payment/add-payments/AddSingleMCPaymentModal'
import _ from 'lodash'
import { InstantFundIsFundedModal } from './fund-payments/InstantFundIsFundedModal'
import useCheckPermissions from 'src/hooks/useCheckPermissions'

interface Props {
  onRefreshPayments: () => void
  onRefreshMCPayments: () => void
  isMCselected: boolean
}

const PaymentsButtonsComponent = ({
  onRefreshPayments,
  onRefreshMCPayments,
  isMCselected,
}: Props) => {
  const dispatch = useDispatch()

  const { hasPermission } = useCheckPermissions()

  const { reload } = useContext(ReloadWrapperContext)

  const partnerId = useSelector(getPartnerId)

  const [fundingMethods, setFundingMethods] = useState<FundingMethod[] | null>(null)
  const [paymentsSummary, setPaymentsSummary] = useState<PaymentsSummary | null>(null)
  const [hasRequiresFundingPayments, setHasRequiresFundingPayments] = useState<boolean>(false)
  const [currentInvoice, setCurrentInvoice] = useState<FundingInvoice | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [fundingSettingsBankAccountDetails, setFundingSettingsBankAccountDetails] =
    useState<FundingSettingsBankAccountDetails | null>(null)

  const {
    isOpen: isChooseFundMethodOpen,
    open: openChooseFundMethod,
    close: closeChooseFundMethod,
  } = useModalOpen()
  const {
    isOpen: isInstantFundConfirmOpen,
    open: openInstantFundConfirm,
    close: closeInstantFundConfirm,
  } = useModalOpen()
  const {
    isOpen: isBankFundConfirmOpen,
    open: openBankFundConfirm,
    close: closeBankFundConfirm,
  } = useModalOpen()
  const {
    isOpen: isInstantFundIsFundedOpen,
    open: openInstantFundIsFunded,
    close: closeInstantFundIsFunded,
  } = useModalOpen()
  const {
    isOpen: isInstantFundInProgressOpen,
    open: openInstantFundInProgress,
    close: closeInstantFundInProgress,
  } = useModalOpen()
  const {
    isOpen: isBankFundInProgressOpen,
    open: openBankFundInProgress,
    close: closeBankFundInProgress,
  } = useModalOpen()
  const {
    isOpen: isNothingToFundOpen,
    open: openNothingToFund,
    close: closeNothingToFund,
  } = useModalOpen()
  const {
    isOpen: isUploadPaymentsModalOpen,
    open: openUploadPaymentsModal,
    close: closeUploadPaymentsModal,
  } = useModalOpen()
  const {
    isOpen: isAddSinglePaymentModalOpen,
    open: openAddSinglePaymentModal,
    close: closeAddSinglePaymentModal,
  } = useModalOpen()
  const {
    isOpen: isPaymentsSummaryOpen,
    open: openPaymentsSummary,
    close: closePaymentsSummary,
  } = useModalOpen()
  const {
    isOpen: isUploadMCPaymentsModalOpen,
    open: openUploadMCPaymentsModal,
    close: closeUploadMCPaymentsModal,
  } = useModalOpen()
  const {
    isOpen: isAddSingleMCPaymentModalOpen,
    open: openAddSingleMCPaymentModal,
    close: closeAddSingleMCPaymentModal,
  } = useModalOpen()

  const fetchFundingSettings = useCallback(async () => {
    if (!partnerId) {
      return
    }

    if (!hasPermission(UserPermission.PaymentsProcess)) {
      return
    }

    setIsLoading(true)

    try {
      const {
        methods,
        hasRequiredFundingPayments,
        paymentProvider,
        bankAccountDetails,
      }: FundingSettings = await createAsyncAction(dispatch, FetchFundingSettings.request())

      setFundingMethods(methods)
      setHasRequiresFundingPayments(hasRequiredFundingPayments)

      if (paymentProvider === PartnerPaymentProvider.UsBank) {
        setFundingSettingsBankAccountDetails(bankAccountDetails || null)
      }
    } catch (err) {
      handleApiError(err)
    } finally {
      setIsLoading(false)
    }
  }, [partnerId, hasPermission])

  const fetchPaymentsSummary = useCallback(async () => {
    if (!partnerId) {
      return
    }

    if (!hasPermission(UserPermission.PaymentsProcess)) {
      return
    }

    setIsLoading(true)

    try {
      const paymentsSummary: PaymentsSummary = await createAsyncAction(
        dispatch,
        FetchPaymentsSummary.request(),
      )

      setPaymentsSummary(paymentsSummary)
    } catch (err) {
      handleApiError(err)
    } finally {
      setIsLoading(false)
    }
  }, [partnerId, hasPermission])

  const { hasWireMethod, hasLoanMethod, hasFundingMethods } = useMemo(() => {
    const availableMethods = fundingMethods?.filter((method) => method.available) || []

    const hasWire = availableMethods.some((method) => method.type === FundingType.Wire)
    const hasLoan = availableMethods.some((method) => method.type === FundingType.Loan)

    return {
      hasWireMethod: hasWire,
      hasLoanMethod: hasLoan,
      hasFundingMethods: hasWire || hasLoan,
    }
  }, [fundingMethods])

  const fetchFundingInvoice = useCallback(async () => {
    if (!partnerId || !hasFundingMethods) {
      return
    }

    setIsLoading(true)

    try {
      const currentInvoice: FundingInvoice = await createAsyncAction(
        dispatch,
        FetchFundingInvoice.request(),
      )

      setCurrentInvoice(currentInvoice)
    } catch (err) {
      handleApiError(err)
    } finally {
      setIsLoading(false)
    }
  }, [partnerId, hasFundingMethods])

  const refreshFundingInvoice = useCallback(async () => {
    await fetchFundingInvoice()
  }, [fetchFundingInvoice])

  const handleFundPaymentsClick = useCallback(() => {
    if (
      currentInvoice &&
      currentInvoice.type === FundingType.Wire &&
      currentInvoice.status === FundingInvoiceStatus.Pending
    ) {
      openBankFundInProgress()

      return
    }

    if (
      currentInvoice &&
      currentInvoice.type === FundingType.Loan &&
      currentInvoice.status === FundingInvoiceStatus.Pending
    ) {
      openInstantFundInProgress()

      return
    }

    if (
      currentInvoice &&
      currentInvoice.type === FundingType.Loan &&
      currentInvoice.status === FundingInvoiceStatus.Funded
    ) {
      openInstantFundIsFunded()

      return
    }

    if (hasRequiresFundingPayments && _.isEmpty(currentInvoice)) {
      openPaymentsSummary()

      return
    }

    if (!hasRequiresFundingPayments && _.isEmpty(currentInvoice)) {
      openNothingToFund()

      return
    }

    initiateFundPaymentsFlow()
  }, [reload, hasRequiresFundingPayments, currentInvoice])

  const initiateFundPaymentsFlow = useCallback(async () => {
    if (!partnerId) {
      return
    }

    closePaymentsSummary()

    if (hasWireMethod && hasLoanMethod) {
      openChooseFundMethod()

      return
    }

    if (hasWireMethod) {
      setIsLoading(true)

      try {
        const currentInvoice: FundingInvoice = await createAsyncAction(
          dispatch,
          SubmitFundingMethodRequest.request({ type: FundingType.Wire }),
        )

        handleUpdateCurrentInvoice(currentInvoice)

        openBankFundConfirm()
      } catch (err) {
        handleApiError(err)
      } finally {
        setIsLoading(false)
      }

      return
    }

    if (hasLoanMethod) {
      setIsLoading(true)

      try {
        const currentInvoice: FundingInvoice = await createAsyncAction(
          dispatch,
          SubmitFundingMethodRequest.request({ type: FundingType.Loan }),
        )

        handleUpdateCurrentInvoice(currentInvoice)

        openInstantFundConfirm()
      } catch (err) {
        handleApiError(err)
      } finally {
        setIsLoading(false)
      }

      return
    }
  }, [partnerId, currentInvoice, fundingMethods, isLoading])

  const handleInstantBackToChooseMethod = useCallback(() => {
    closeInstantFundConfirm()
    openChooseFundMethod()
  }, [closeInstantFundConfirm])

  const handleBankBackToChooseMethod = useCallback(() => {
    closeBankFundConfirm()
    openChooseFundMethod()
  }, [closeBankFundConfirm])

  const handleClearCurrentInvoice = useCallback(() => {
    setCurrentInvoice(null)
  }, [])

  const handleUpdateCurrentInvoice = useCallback((invoice: FundingInvoice) => {
    setCurrentInvoice(invoice)
  }, [])

  const handleSinglePaymentSubmit = useCallback(() => {
    reload()
    closeAddSinglePaymentModal()
  }, [reload])

  const handleUploadPaymentsSubmit = useCallback(() => {
    reload()
  }, [reload])

  const handleSingleMCPaymentSubmit = useCallback(() => {
    reload()
    closeAddSingleMCPaymentModal()
  }, [reload])

  useEffect(() => {
    fetchFundingSettings()
  }, [fetchFundingSettings])

  useEffect(() => {
    fetchFundingInvoice()
  }, [fetchFundingInvoice])

  useEffect(() => {
    if (!currentInvoice) {
      return
    }

    if (
      currentInvoice.type === FundingType.Wire &&
      currentInvoice.status === FundingInvoiceStatus.Created
    ) {
      openBankFundConfirm()
    }

    if (
      currentInvoice.type === FundingType.Loan &&
      currentInvoice.status === FundingInvoiceStatus.Created
    ) {
      openInstantFundConfirm()
    }
  }, [currentInvoice])

  useEffect(() => {
    fetchPaymentsSummary()
  }, [fetchPaymentsSummary])

  usePolling({
    interval: 10000,
    shouldStart: () => currentInvoice?.status === FundingInvoiceStatus.Created,
    shouldStop: () => isNonNullableArray(currentInvoice?.invoiceIds),
    callback: refreshFundingInvoice,
  })

  const addPaymentsButton = useMemo(() => {
    if (isMCselected) {
      return (
        <ProtectedWithPermission permission={UserPermission.PaymentsCreate}>
          <AddMCPaymentsActionsButton
            currentInvoice={currentInvoice}
            onOpenBankFundInProgress={openBankFundInProgress}
            onOpenInstantFundInProgress={openInstantFundInProgress}
            onOpenUploadMCPaymentsModal={openUploadMCPaymentsModal}
            onOpenAddSingleMCPaymentModal={openAddSingleMCPaymentModal}
          />
        </ProtectedWithPermission>
      )
    }

    return (
      <ProtectedWithPermission permission={UserPermission.PaymentsCreate}>
        <AddPaymentsActionsButton
          currentInvoice={currentInvoice}
          onOpenBankFundInProgress={openBankFundInProgress}
          onOpenInstantFundInProgress={openInstantFundInProgress}
          onOpenUploadPaymentsModal={openUploadPaymentsModal}
          onOpenAddSinglePaymentModal={openAddSinglePaymentModal}
        />
      </ProtectedWithPermission>
    )
  }, [
    isMCselected,
    currentInvoice,
    openBankFundInProgress,
    openInstantFundInProgress,
    openUploadPaymentsModal,
    openAddSinglePaymentModal,
  ])

  const processPaymentsButton = useMemo(() => {
    if (isMCselected) {
      return (
        <ProtectedWithPermission permission={UserPermission.PaymentsProcess}>
          <ProcessMCPaymentsButton />
        </ProtectedWithPermission>
      )
    }

    return (
      <ProtectedWithPermission permission={UserPermission.PaymentsProcess}>
        <ProcessPaymentsButton />
      </ProtectedWithPermission>
    )
  }, [isMCselected])

  return (
    <Fragment>
      {addPaymentsButton}

      <ProtectedWithPermission permission={UserPermission.PaymentsProcess}>
        {hasFundingMethods ? (
          <Button
            data-testid={
              isMCselected
                ? 'payments-mc-main-fund-payments-btn'
                : 'payments-main-fund-payments-btn'
            }
            className={tw`ml-4`}
            size="small"
            color="dark"
            rightIcon={<MarkPaidMinor width={20} height={20} fill="white" />}
            onClick={handleFundPaymentsClick}
            disabled={isLoading}
          >
            Fund settlements
          </Button>
        ) : (
          processPaymentsButton
        )}
      </ProtectedWithPermission>
      <ChooseFundMethodModal
        isOpen={isChooseFundMethodOpen}
        onClose={closeChooseFundMethod}
        onInstant={openInstantFundConfirm}
        onBankTransfer={openBankFundConfirm}
        handleUpdateCurrentInvoice={handleUpdateCurrentInvoice}
      />
      <InstantFundConfirmModal
        isOpen={isInstantFundConfirmOpen}
        onBack={
          hasLoanMethod && hasWireMethod ? handleInstantBackToChooseMethod : closeInstantFundConfirm
        }
        onClose={closeInstantFundConfirm}
        onApproveAndFund={openInstantFundInProgress}
        currentInvoice={currentInvoice}
        refreshFundingInvoice={refreshFundingInvoice}
        onRefreshPayments={onRefreshPayments}
        onRefreshMCPayments={onRefreshMCPayments}
      />
      <BankFundConfirmModal
        isOpen={isBankFundConfirmOpen}
        onBack={hasLoanMethod ? handleBankBackToChooseMethod : closeBankFundConfirm}
        onClose={closeBankFundConfirm}
        onCreateInvoice={openBankFundInProgress}
        currentInvoice={currentInvoice}
        refreshFundingInvoice={refreshFundingInvoice}
        onRefreshPayments={onRefreshPayments}
        onRefreshMCPayments={onRefreshMCPayments}
      />
      <InstantFundInProgressModal
        isOpen={isInstantFundInProgressOpen}
        onClose={closeInstantFundInProgress}
        currentInvoice={currentInvoice}
        handleClearCurrentInvoice={handleClearCurrentInvoice}
      />
      <InstantFundIsFundedModal
        isOpen={isInstantFundIsFundedOpen}
        onClose={closeInstantFundIsFunded}
        currentInvoice={currentInvoice}
        handleClearCurrentInvoice={handleClearCurrentInvoice}
      />
      <BankFundInProgressModal
        isOpen={isBankFundInProgressOpen}
        currentInvoice={currentInvoice}
        bankAccountDetails={fundingSettingsBankAccountDetails}
        onClose={closeBankFundInProgress}
        handleClearCurrentInvoice={handleClearCurrentInvoice}
        refreshFundingInvoice={refreshFundingInvoice}
      />
      <NothingToFundModal
        isOpen={isNothingToFundOpen}
        onClose={closeNothingToFund}
        onOpenUploadPaymentsModal={
          isMCselected ? openUploadMCPaymentsModal : openUploadPaymentsModal
        }
        onOpenAddSinglePaymentModal={
          isMCselected ? openAddSingleMCPaymentModal : openAddSinglePaymentModal
        }
      />
      <UploadPaymentsCSVModal
        isOpen={isUploadPaymentsModalOpen}
        onClose={closeUploadPaymentsModal}
        onSubmit={handleUploadPaymentsSubmit}
      />
      <AddSinglePaymentModal
        isOpen={isAddSinglePaymentModalOpen}
        onClose={closeAddSinglePaymentModal}
        onSubmit={handleSinglePaymentSubmit}
      />
      <UploadMCPaymentsCSVModal
        isOpen={isUploadMCPaymentsModalOpen}
        onClose={closeUploadMCPaymentsModal}
        onSubmit={reload}
      />
      <AddSingleMCPaymentModal
        isOpen={isAddSingleMCPaymentModalOpen}
        onClose={closeAddSingleMCPaymentModal}
        onSubmit={handleSingleMCPaymentSubmit}
      />
      <PaymentsSummaryModal
        isOpen={isPaymentsSummaryOpen}
        onClose={closePaymentsSummary}
        onContinue={initiateFundPaymentsFlow}
        paymentsSummary={paymentsSummary}
      />
    </Fragment>
  )
}

export const PaymentsButtons = memo(PaymentsButtonsComponent)
